import { getCurrentInstance, onUnmounted } from 'vue';
import { Auth } from 'aws-amplify';
import awsExports from '@/aws-exports';
import Store, { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';

interface SessionExpiry {
  markSessionExpired: () => void;
  redirectToLogin: (returnPath?: string) => Promise<void>;
  probeSession: () => Promise<void>;
  watchForExpiredSession: () => () => void;
  resetProbeThrottle: () => void;
}

// Thrown in place of a request that would only 401. Distinct from an axios error so
// Sentry can drop it without also dropping genuine 401s.
export class SessionExpiredError extends Error {
  constructor() {
    super('Session has expired');
    this.name = 'SessionExpiredError';
  }
}

const CLIENT_ID = awsExports.userPoolWebClientId;
const REGION = awsExports.region;
const STORAGE_PREFIX = `CognitoIdentityServiceProvider.${CLIENT_ID}`;
const PROBE_THROTTLE_MS = 30_000;
// getAuthHeaders awaits this on the critical path of every request, so a hung connection
// must not be allowed to stall the app indefinitely — fetch has no default timeout.
const PROBE_TIMEOUT_MS = 5_000;

// Cognito returns NotAuthorizedException for causes unrelated to expiry (a missing
// SECRET_HASH, a required device key). Expiry is a permanent latch, so require the message
// to name the refresh token before locking anyone out. Safe to match on text here because
// this is Cognito's raw response, not one of Amplify's wrapped messages.
const DEAD_REFRESH_TOKEN = /refresh token/i;

let inFlightProbe: Promise<void> | null = null;
let lastProbeAt = 0;

function readStored(kind: 'idToken' | 'refreshToken' | 'deviceKey' | 'clockDrift'): string | null {
  try {
    const username = localStorage.getItem(`${STORAGE_PREFIX}.LastAuthUser`);
    return username ? localStorage.getItem(`${STORAGE_PREFIX}.${username}.${kind}`) : null;
  } catch {
    return null;
  }
}

function idTokenExpiry(token: string | null): number | null {
  if (!token) {
    return null;
  }
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const { exp } = JSON.parse(json) as { exp?: number };
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

// Mirrors CognitoUserSession.isValid(), which compares (now - clockDrift) against the token
// expiry (CognitoUserSession.js:76-79). Using a raw clock here would let the gate disagree
// with Amplify: on a slow clock it would judge a dead token live, skip the probe, and hand
// the dead session to currentSession() — the redirect this whole design avoids.
function idTokenHasLapsed(): boolean | null {
  const exp = idTokenExpiry(readStored('idToken'));
  if (exp === null) {
    return null;
  }
  const clockDrift = Number(readStored('clockDrift')) || 0;
  return Math.floor(Date.now() / 1000) - clockDrift >= exp;
}

type RefreshVerdict = 'alive' | 'expired' | 'unknown';

// Asks Cognito directly rather than going through Auth.currentSession(). Amplify reacts to
// an invalid session by running cleanUpInvalidSession, which for hosted-UI sign-ins starts
// a full-page redirect to the Cognito logout endpoint — the page would be gone before the
// modal could render.
async function askCognito(refreshToken: string, deviceKey: string | null): Promise<RefreshVerdict> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), PROBE_TIMEOUT_MS);
  try {
    const response = await fetch(`https://cognito-idp.${REGION}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        ClientId: CLIENT_ID,
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        // Amplify attaches DEVICE_KEY when one is stored (CognitoUser.js:1169-1173).
        // Without it this would not be the same request Amplify makes, and a
        // device-tracking pool would reject a refresh token that is actually fine.
        AuthParameters: deviceKey
          ? { REFRESH_TOKEN: refreshToken, DEVICE_KEY: deviceKey }
          : { REFRESH_TOKEN: refreshToken },
      }),
      signal: abort.signal,
    });

    if (response.ok) {
      // The fresh tokens are deliberately discarded: Amplify refreshes on its own for the
      // next real request. Safe only while refresh token rotation stays disabled — with
      // rotation on, this call would invalidate the token Amplify still holds.
      return 'alive';
    }

    const body = (await response.json()) as { __type?: string; message?: string };
    const rejected = body.__type?.includes('NotAuthorizedException') ?? false;
    return rejected && DEAD_REFRESH_TOKEN.test(body.message ?? '') ? 'expired' : 'unknown';
  } catch {
    return 'unknown';
  } finally {
    clearTimeout(timer);
  }
}

// Expiry is a one-way latch: once Cognito says the refresh token is dead the only way back
// is a full re-auth, so nothing here ever clears the flag. Signing out clears it via the
// LOGOUT mutation instead.
export const useSessionExpiry = (): SessionExpiry => {
  const markSessionExpired = (): void => {
    Commit(StoreMutationType.SET_SESSION_EXPIRED, true);
  };

  const redirectToLogin = async (returnPath = `${window.location.pathname}${window.location.search}`): Promise<void> => {
    await Auth.federatedSignIn({
      customProvider: '',
      customState: returnPath,
    });
  };

  const askCognitoThrottled = async (refreshToken: string): Promise<void> => {
    // Throttling guards only the network round-trip; the local expiry check is free, and
    // skipping it would leave a window in which a session that died goes unnoticed.
    if (Date.now() - lastProbeAt < PROBE_THROTTLE_MS) {
      return;
    }

    const verdict = await askCognito(refreshToken, readStored('deviceKey'));
    if (verdict === 'unknown') {
      // Nothing was established, so this must not suppress the next attempt: treating
      // silence as liveness is what lets getAuthHeaders fall through to Amplify.
      return;
    }

    lastProbeAt = Date.now();
    if (verdict === 'expired') {
      markSessionExpired();
    }
  };

  const runProbe = async (): Promise<void> => {
    if (idTokenHasLapsed() !== true) {
      return;
    }

    const refreshToken = readStored('refreshToken');
    if (!refreshToken) {
      return;
    }

    await askCognitoThrottled(refreshToken);
  };

  const probeSession = async (): Promise<void> => {
    if (Store.state.sessionExpired) {
      return;
    }
    if (inFlightProbe) {
      return inFlightProbe;
    }

    inFlightProbe = runProbe().finally(() => {
      inFlightProbe = null;
    });
    return inFlightProbe;
  };

  const resetProbeThrottle = (): void => {
    inFlightProbe = null;
    lastProbeAt = 0;
  };

  // Timers are throttled in background tabs and skipped entirely while the machine sleeps,
  // so refocus is the only reliable moment to notice a session that died while away.
  // visibilitychange covers tab switches and minimising; switching to another application
  // leaves the tab visible and only fires window focus.
  const watchForExpiredSession = (): (() => void) => {
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void probeSession();
      }
    };
    const onFocus = (): void => {
      void probeSession();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    const stop = (): void => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };

    // Callers outside a component (tests, app bootstrap) get the returned teardown instead.
    if (getCurrentInstance()) {
      onUnmounted(stop);
    }
    return stop;
  };

  return { markSessionExpired, redirectToLogin, probeSession, watchForExpiredSession, resetProbeThrottle };
};
